import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNegotiation } from '@hooks/apiHooks/sessionHooks';
import { NegotiateParams, NegotiateType } from '../../../api/session';
import { useSessionFeatureState } from '../../../hooks/storeHooks/useSessionState';
import { NegotiateSocketMessage } from '../../../types/socketMessage';
import { WebRTCMessage } from '../../../types/webRtcMessage';
import { MediaCapture } from '../../../utils/mediaCapture';
import { Microphone } from './useMicrophones';
import { Features } from '../../../config.ts';
import { buildMusicGenerationInitialSpeech } from '../constants/musicGenerationInitialSpeech';
import {
  MUSIC_ALBUM_COVER_REGENERATION,
  MUSIC_LYRICS_REGENERATION,
  MUSIC_SONG_TITLE_REGENERATION,
  REGENERATE_CREATION,
} from '../constants/musicGenerationRealtimeTools';

interface useSessionWebRTCProps {
  avatarId: string;
  userMicrophoneStream: MediaStream | null;
  selectedMicrophone: Microphone | null;
  mentorId: string;
  profileData?: {
    linkedInSummary: string | null;
    profileJson: string | null;
  };
  context?: string;
  companionSlug?: string;
  isCapabilitySession: boolean;
  generationPrompt?: string;
  capabilityType?: string;
  negotiateType: NegotiateType;
}

const rtcConfig: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun1.ciscape.com:3478' },
    {
      urls: 'turn:stun1.ciscape.com:3478',
      username: 'tcuser',
      credential: 'dw4k_BR2f-Y4A-@aT7ip@Qzs',
    },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

let prevMessages: WebRTCMessage[] = [];

const getPrevMessages = () => {
  const messages = prevMessages;
  prevMessages = [];
  return messages;
};

export const useSessionWebRTC = ({
  avatarId,
  userMicrophoneStream,
  selectedMicrophone,
  mentorId,
  context,
  profileData,
  isCapabilitySession,
  generationPrompt,
  capabilityType,
  negotiateType,
}: useSessionWebRTCProps) => {
  const { t } = useTranslation();
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioReady, setAudioReady] = useState(false);
  const openAiSessionRef = useRef<string | null>(null);
  const { mutateAsync: doNegotiation } = useNegotiation();

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioSenderRef = useRef<RTCRtpTransceiver | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentSocketIdRef = useRef<string | null>(null);

  const {
    prevMentorId,
    prevSocketId,
    isSessionPage,
    setDataChannel,
    setSessionId,
    setSocketId,
    setPrevSocketId,
    setPrevMentorId,
    setWebRTCStatus,
  } = useSessionFeatureState();
  const screenShareCaptureRef = useRef<MediaCapture | null>(null);

  useEffect(() => {
    screenShareCaptureRef.current = new MediaCapture({
      interval: 1000,
    });

    // Cleanup MediaCapture worker on unmount
    return () => {
      if (screenShareCaptureRef.current) {
        screenShareCaptureRef.current.terminate();
        screenShareCaptureRef.current = null;
      }
    };
  }, []);

  const handleMessage = useCallback((evt: { data: string; type: string }) => {
    const data = JSON.parse(evt.data) as WebRTCMessage;
    if (data?.data?.message?.reason === 'content_filter') {
      console.log(
        `%cContent filter ${
          openAiSessionRef.current ? openAiSessionRef.current : ''
        }`,
        'color: #0062ff',
      );
      console.log('prev messages:', getPrevMessages());
    }
    if (data?.data?.message?.session?.id) {
      openAiSessionRef.current = data?.data?.message?.session?.id;
    }
  }, []);

  const setInstructions = (promptData: string) => {
    if (dataChannelRef.current) {
      const dataSettings = {
        type: 'set_settings',
        data: {
          instructions: `The user has provided an additional file containing information: ${JSON.stringify(
            promptData,
          )}`,
          temperature: 1,
        },
      };
      console.log('set_settings', promptData);
      dataChannelRef.current.send(JSON.stringify(dataSettings));
    }
  };

  const handleConnectionStateChange = useCallback(() => {
    if (!pcRef.current) return;
    const { connectionState } = pcRef.current;
    console.log('Connection state changed:', connectionState);
    switch (connectionState) {
      case 'failed':
        setWebRTCStatus({
          blocked: true,
          reason: 'WebRTC connection failed',
          message:
            'It looks like your network is unstable or blocking WebRTC. Try disabling your VPN or switching to a different network.',
        });
        break;
      case 'connected':
        setWebRTCStatus({ blocked: false, reason: '', message: '' });
        console.log('Closing signaling socket');
        socketRef.current?.close();
        socketRef.current = null;
        break;
      default:
        break;
    }
  }, []);

  const handleGetTrack = useCallback((evt: RTCTrackEvent) => {
    const stream = evt.streams[0];
    if (evt.track.kind === 'video') {
      console.log('Video track received');
      setVideoStream(stream);
    } else {
      console.log('Audio track received');
      setAudioStream(stream);
    }
  }, []);

  const handleSocketConnection = async () => {
    let websocket: WebSocket | null = null;
    try {
      console.log('Connecting to signaling server');

      // Create a new AbortController for this connection attempt
      abortControllerRef.current = new AbortController();

      const negotiateParams: NegotiateParams = {
        mentorId,
        useIdleTimeOut: false,
        avatarInfo: {
          id: avatarId,
          useGreenVideo: false,
        },
      };

      if (isCapabilitySession) {
        negotiateParams.avatarInfo.libraryId = 'Companion.Desktop';
        negotiateParams.avatarInfo.functions =
          capabilityType === 'music'
            ? [
                MUSIC_LYRICS_REGENERATION,
                MUSIC_ALBUM_COVER_REGENERATION,
                MUSIC_SONG_TITLE_REGENERATION,
              ]
            : [REGENERATE_CREATION];
      }

      if (context) {
        negotiateParams.userContextId = context;
      }

      // Only include prevMentorId and prevSocketId for session page
      if (isSessionPage && prevMentorId) {
        negotiateParams.prevMentorId = prevMentorId;
      }

      if (isSessionPage && prevSocketId) {
        negotiateParams.prevSocketId = prevSocketId;
      }

      if (profileData?.linkedInSummary) {
        negotiateParams.linkedInSummary = profileData.linkedInSummary;
      }

      if (profileData?.profileJson) {
        negotiateParams.profileJson = profileData.profileJson;
      }

      if (generationPrompt) {
        if (capabilityType === 'music') {
          negotiateParams.initialSpeech = buildMusicGenerationInitialSpeech(
            generationPrompt,
            Features.enableSingleSongMode,
          );
        } else if (capabilityType === 'design') {
          negotiateParams.initialSpeech =
            'The user has asked to generate a new design. The prompt is: ' +
            generationPrompt +
            ". The system is generating two distinct variations of the design on your behalf. If the user inquires about the design, inform them that it's currently being created. Once the designs are ready, you will receive a notification. Meanwhile, continue keeping the conversation naturally engaging with the user. You are able to create images, you are also able regenerate any of those.";
        }
      }

      const { connection, socket_id } = await doNegotiation({
        params: negotiateParams,
        signal: abortControllerRef.current.signal,
        type: negotiateType,
      });

      console.log('waiting for websocket open');
      websocket = new WebSocket(
        `${connection.signaling_endpoint}/ws/connections/${connection.id}/signaling`,
      );

      if (socket_id) {
        currentSocketIdRef.current = socket_id;
        setSocketId(socket_id);
        if (isSessionPage) {
          setPrevSocketId(socket_id);
          setPrevMentorId(mentorId);
        }
      }

      const waitForWebsocketOpen = new Promise((resolve, reject) => {
        websocket!.onopen = resolve;
        websocket!.onclose = reject;
        websocket!.onerror = () => {
          reject(new Error('WebSocket error'));
        };
      });

      await waitForWebsocketOpen;
      console.log('websocket connected');

      socketRef.current = websocket;

      setSessionId(connection.id);
    } catch (error: unknown) {
      // Clean up websocket if connection failed
      if (websocket && websocket.readyState !== WebSocket.CLOSED) {
        websocket.close();
      }

      if (
        error &&
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        (error as { message?: string }).message !== 'canceled'
      ) {
        const err = error as {
          response?: { status?: number };
          message?: string;
        };

        // Rate limit (429) — show a specific message so users know the
        // error is transient and tied to their rapid retries, not a
        // persistent service outage.
        const isRateLimited = err?.response?.status === 429;

        setWebRTCStatus({
          errorStatus: err?.response?.status,
          blocked: true,
          reason: `WebRTC negotiation error: ${err?.message}`,
          message: isRateLimited ? t('avatar.rateLimited') : t('avatar.highDemand'),
        });
      }
    }
  };

  const sendSignal = (type: unknown, data: unknown) => {
    if (socketRef.current) {
      console.log('Sending signal', type, data);
      socketRef.current.send(
        JSON.stringify({
          type,
          data,
        }),
      );
    }
  };

  const handleSocketMessage = (pc: RTCPeerConnection) => {
    if (socketRef.current) {
      socketRef.current.onmessage = async (event: {
        data: string;
        type: string;
      }) => {
        if (event.type == 'message') {
          const { data, type } = JSON.parse(
            event.data,
          ) as NegotiateSocketMessage;
          switch (type) {
            case 'set_remote_description':
              console.log('Setting remote description', data);
              await pc.setRemoteDescription(data);
              break;
            case 'add_ice_candidate':
              if (data.candidate) {
                console.log('Received ICE candidate');
                await pc.addIceCandidate(data.candidate);
              }
              break;
            case 'error':
              socketRef.current?.close();
              socketRef.current = null;
              break;
          }
        }
      };
    }
  };

  const negotiateWithSignaling = async (pc: RTCPeerConnection) => {
    console.log('Creating offer');
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        sendSignal('add_ice_candidate', { candidate });
      }
    };

    await pc.setLocalDescription();
    sendSignal('set_remote_description', pc.localDescription);
  };

  const clearPeerConnectionHandlers = (pc: RTCPeerConnection) => {
    pc.onicecandidate = null;
  };

  const startWebrtc = async (micStream?: MediaStream | null) => {
    try {
      await handleSocketConnection();

      const pc = new RTCPeerConnection(rtcConfig);
      pcRef.current = pc;
      handleSocketMessage(pc);

      pc.addEventListener('connectionstatechange', handleConnectionStateChange);
      pc.addEventListener('track', handleGetTrack);

      const dataChannel = pc.createDataChannel('chat', { ordered: true });
      dataChannelRef.current = dataChannel;
      setDataChannel(dataChannel);

      dataChannel.onopen = () => {
        console.log('DataChannel opened, ready for screen sharing commands');
      };
      dataChannel.onclose = () => {
        console.log('DataChannel closed');
      };
      dataChannel.onerror = (error) => {
        console.error('DataChannel error:', error);
      };
      dataChannel.onmessage = handleMessage;

      const audioSendTransceiver = pc.addTransceiver('audio', {
        direction: 'sendrecv',
      });
      audioSenderRef.current = audioSendTransceiver;

      const canvas = screenShareCaptureRef.current?.getCanvas();

      if (!canvas) {
        throw new Error('Canvas not available for screen sharing');
      }
      const stream = canvas.captureStream(1);
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      setAudioReady(true);

      // Prefer the directly-passed mic stream over React state, which may be
      // stale when connectMicrophone() and startWebrtc() are called back-to-back
      // (setStream hasn't re-rendered yet).
      const effectiveMicStream = micStream ?? userMicrophoneStream;
      if (
        effectiveMicStream &&
        effectiveMicStream.getAudioTracks().length > 0
      ) {
        const audioTrack = effectiveMicStream.getAudioTracks()[0];
        await audioSendTransceiver.sender.replaceTrack(audioTrack);
      }

      pc.addTransceiver('video', { direction: 'recvonly' });

      // Negotiate AFTER all tracks/transceivers/data channels are added
      await negotiateWithSignaling(pc);
    } catch (error) {
      console.error('Failed to start WebRTC:', error);
      // Clean up any partially created resources
      stopWebrtc();
      throw error;
    }
  };

  const stopWebrtc = () => {
    if (currentSocketIdRef.current) {
      setPrevSocketId(currentSocketIdRef.current);
    }

    // Clear the ref
    currentSocketIdRef.current = null;

    // Abort any pending negotiate request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setDataChannel(null);

    // Close the DataChannel and clear all event handlers
    if (dataChannelRef.current) {
      dataChannelRef.current.onmessage = null;
      dataChannelRef.current.onopen = null;
      dataChannelRef.current.onclose = null;
      dataChannelRef.current.onerror = null;
      dataChannelRef.current.close();
    }
    dataChannelRef.current = null;

    // Replace audio track BEFORE closing the peer connection
    if (audioSenderRef.current) {
      audioSenderRef.current.sender
        .replaceTrack(null)
        .catch((error: unknown) => {
          console.warn('Failed to replace audio track during cleanup:', error);
        });
      audioSenderRef.current = null;
    }

    if (pcRef.current) {
      // Stop all sender tracks
      pcRef.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });

      // Stop all receiver tracks (received media streams)
      pcRef.current.getReceivers().forEach((receiver) => {
        if (receiver.track) {
          receiver.track.stop();
        }
      });

      // Remove event listeners
      pcRef.current.removeEventListener(
        'connectionstatechange',
        handleConnectionStateChange,
      );
      pcRef.current.removeEventListener('track', handleGetTrack);

      // Clear direct property handlers
      clearPeerConnectionHandlers(pcRef.current);
    }

    // Close the PeerConnection after cleaning up tracks
    pcRef.current?.close();
    pcRef.current = null;

    // Close WebSocket and clear handlers
    if (socketRef.current) {
      socketRef.current.onmessage = null;
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.close();
    }
    socketRef.current = null;

    // Reset state streams
    setVideoStream(null);
    setAudioStream(null);
    setAudioReady(false);
    setWebRTCStatus({
      blocked: false,
      reason: '',
      message: '',
    });
  };

  useEffect(() => {
    if (
      pcRef.current &&
      audioSenderRef.current &&
      userMicrophoneStream &&
      audioReady
    ) {
      console.log('Replacing audio track');
      const newAudioTrack = userMicrophoneStream.getAudioTracks()[0];
      if (newAudioTrack) {
        audioSenderRef.current.sender
          .replaceTrack(newAudioTrack)
          .catch((error: unknown) =>
            console.error('Failed to replace audio track:', error),
          );
      }
    }
  }, [userMicrophoneStream, selectedMicrophone, audioReady]);

  return {
    videoStream,
    audioStream,
    isWebRtcConnected:
      pcRef.current?.connectionState === 'connected' &&
      dataChannelRef.current?.readyState === 'open',
    stopWebrtc,
    startWebrtc,
    setInstructions,
    screenShareCaptureRef,
  };
};
